import React, { useState, useEffect } from 'react';
import * as _ from 'lodash';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { subscribe } from 'react-contextual';

import { createSidebar } from '../../redux/actions/general/sidebar';
import {
  removePlusActions,
  setPlusActions,
} from '../../redux/actions/general/plus';
import {
  getStagesPermissions,
  filterProjectSubstages,
  listProjectSubstages,
  readStage,
  updateStage,
  updateStageStatus,
  updateSubstageStatus,
} from '../../redux/actions/table/stages';
import {
  __,
  getActiveStage,
  getLocalized,
  redirect,
  setActiveProject,
  setTableParams,
} from '../../core/utils';
import {
  enableProjectsDropdown,
  listActiveModules,
} from '../../redux/actions/general/active_modules';
import styles from '../../styles/views/stages/Stages.scss';
import { withRouter } from 'react-router';
import Localization from '../../helpers/Localization';
import {
  createSubstage,
  readSubstage,
  updateSubstage,
} from '../../redux/actions/table/substages';
import {
  createTask,
  readTask,
  updateTask,
  updateTaskToolbar,
  updateTaskStatus,
} from '../../redux/actions/table/all_tasks';
import { readProjectGeneral } from '../../redux/actions/projects';
import { getPreferences, updatePreferences } from '../../redux/actions/profile';
import TitleBar from '../../containers/TitleBar';
import { setNotification } from '../../redux/actions/general/notification';
import { hideDropdown } from '../../redux/actions/general/dropdown';
import { listPlanningLibraryItems } from '../../redux/actions/table/planning_library';
import StagesFlyout from '../../flyouts/stages/StagesFlyout';
import SubstagesFlyout from '../../flyouts/substages/SubstagesFlyout';
import TasksFlyout from '../../flyouts/tasks/TasksFlyout';
import filterStyles from '../../styles/views/boq/WorkingSetBOQ.scss';
import ProjectPlanningTable from './helpers/ProjectPlanningTable';
import ProjectPlanningLibraryDropdown from './helpers/ProjectPlanningLibraryDropdown';
import ArrowRightSmallIcon from '../../assets/images/arrow-right-small-15x15.svg';

import { ContextMenu, hideMenu, MenuItem, SubMenu } from 'react-contextmenu';

const PLANNING_LIBRARY_TYPES = ['stage', 'substage'];

const emptyTemplateCodes = () => ({
  stage: {},
  substage: {},
});

const ProjectPlanning = ({
  actions,
  table,
  taskTable,
  match,
  localization,
  dropdownName,
  clicked_stage_row,
  clicked_substage_row,
  main_language: mainLanguage,
}) => {
  const closePlanningTask = () => {
    setTableParams('stages', { clicked_task_row: undefined });
    setTableParams('all_tasks', { clicked_row: undefined });
    listProjectSubstages(match.params.project);
  };

  const openPlanningTask = (taskId) => {
    setTableParams('stages', {
      clicked_task_row: taskId,
      clicked_stage_row: null,
      clicked_substage_row: null,
    });
    setTableParams('all_tasks', { clicked_row: taskId, flyout: undefined });
    readTask(taskId);
  };

  const [expandedRows, setExpandedRows] = useState(null);
  const [focusedId, setFocusedId] = useState([]);
  const [isProjectAdministrator, setIsProjectAdministrator] = useState(false);
  const [hasPlanningModuleVisibility, setHasPlanningModuleVisibility] =
    useState(null);

  const [focusedRow, setFocusedRow] = useState();
  const [libraryPicker, setLibraryPicker] = useState();
  const [templateCodes, setTemplateCodes] = useState(emptyTemplateCodes);

  useEffect(() => {
    let active = true;

    getPreferences('table.project_planning').then(({ response: { data } }) => {
      if (active && _.isArray(data?.disabled_columns)) {
        setTableParams('stages', {
          disabled_columns: data.disabled_columns,
        });
      }
    });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    setTemplateCodes(emptyTemplateCodes());

    if (!hasPlanningModuleVisibility) {
      return () => {
        active = false;
      };
    }

    const requests = PLANNING_LIBRARY_TYPES.map((type) =>
      listPlanningLibraryItems(type).then(
        ({ response }) => [
          type,
          _.mapValues(_.keyBy(response.data, 'id'), 'code'),
        ],
        () => [type, {}],
      ),
    );

    Promise.all(requests).then((codes) => {
      if (active) setTemplateCodes(_.fromPairs(codes));
    });

    return () => {
      active = false;
    };
  }, [match.params.project, hasPlanningModuleVisibility]);

  useEffect(() => {
    removePlusActions();
    setActiveProject(match.params.project);

    getStagesPermissions(match.params.project).then(({ response }) => {
      const is_project_administrator = _.get(response.data, 'administrator');
      const canViewProjectPlanning = _.includes(
        _.get(response.data, 'planning_module_visibility.permissions', []),
        'planning_module_visibility',
      );

      createSidebar({
        title: __('submenu.title.project'),
        items: _.compact([
          {
            title: __('submenu.title.project-properties'),
            link: () => '/projects/' + match.params.project + '/general',
          },
          canViewProjectPlanning && {
            title: __('submenu.title.project-planning'),
            link: () =>
              '/projects/' + match.params.project + '/project-planning',
            active: true,
          },
          {
            title: __('submenu.title.teams'),
            link: () => '/stages/' + getActiveStage() + '/teams',
          },
          {
            title: __('submenu.title.stages'),
            link: () => '/projects/' + match.params.project + '/stages',
          },
          {
            title: __('submenu.title.approvers'),
            link: () => '/stages/' + getActiveStage() + '/approvers',
          },
          {
            title: __('submenu.title.import_spreadsheet'),
            link: () => '/import/' + getActiveStage(),
          },
        ]),
      });

      canViewProjectPlanning &&
        is_project_administrator &&
        setPlusActions([
          {
            name: __('wizard.add-stage.name.create-new-stage'),
            onClick: () =>
              actions.setTableParams({
                wizard_active: true,
              }),
          },
          {
            name: __('wizard.add-stage.name.create-new-substage'),
            onClick: () => {
              setTableParams('substages', {
                wizard_active: true,
              });
            },
            disabled: _.size(table.data) > 0,
          },
          {
            name: __('wizard.add-task.name.create-new-task'),
            onClick: () =>
              actions.setTableParams({
                add_task_from_project_planning: true,
              }),
          },
          {
            name: __('project_planning.load_template.action'),
            onClick: () =>
              actions.setTableParams({
                load_project_planning_from_template: {
                  project_id: match.params.project,
                },
              }),
          },
        ]);

      setHasPlanningModuleVisibility(canViewProjectPlanning);
      setIsProjectAdministrator(is_project_administrator);

      canViewProjectPlanning && listProjectSubstages(match.params.project);
    });

    return () => {
      removePlusActions();
      setTableParams('stages', { clicked_task_row: undefined });
      setTableParams('all_tasks', { clicked_row: undefined });
    };
  }, [match.params.project]);

  useEffect(() => {
    if (expandedRows !== null || _.isEmpty(table?.substages)) {
      return;
    }

    const defaultExpanded = _.reduce(
      table.substages,
      (ids, group) => {
        !_.isEmpty(group.children) && ids.push(group.id);
        return ids;
      },
      [],
    );

    setExpandedRows(defaultExpanded);
  }, [expandedRows, table?.substages]);

  useEffect(() => {
    listActiveModules();
    setActiveProject(match.params.project);

    enableProjectsDropdown(({ project_id }) => {
      getStagesPermissions(project_id).then(({ response }) => {
        const canViewProjectPlanning = _.includes(
          _.get(response.data, 'planning_module_visibility.permissions', []),
          'planning_module_visibility',
        );

        redirect(
          '/projects/' +
            project_id +
            (canViewProjectPlanning ? '/project-planning' : '/stages'),
        );
      });
    });

    readProjectGeneral(match.params.project).then(({ response }) => {
      setTableParams('stages', {
        main_language: response.data.main_language,
        project_id: match.params.project,
      });

      listActiveModules();
    });
  }, [match.params.project]);

  const _finishUpdate = (notification) => {
    listProjectSubstages(match.params.project);
    setNotification(notification);
    hideDropdown();
  };

  const _updateSubstage = (id, data) =>
    updateSubstage(id, data).then(() =>
      _finishUpdate(__('project.substages.notification.success-update')),
    );

  const _updateStage = (id, data) => {
    return updateStage(id, data).then(() =>
      _finishUpdate(__('project.stages.notification.success-update')),
    );
  };

  const _updatePlanningTask = (id, changes) => {
    let request;

    if (changes.date) {
      request = readTask(id).then(({ response }) => {
        const task = response.data;
        return updateTask(id, {
          title: task.title,
          description: task.description,
          tags: task.tags,
          date: { ...task.date, ...changes.date },
        });
      });
    } else if (changes.status) {
      request = updateTaskStatus(id, changes.status);
    } else {
      request = updateTaskToolbar(id, changes);
    }

    return request.then(() => _finishUpdate(__('tasks.properties.saved')));
  };

  const _setStageStatus = (id, status) =>
    updateStageStatus(id, status).then(() =>
      _finishUpdate(__('project.stages.notification.success-update')),
    );

  const _setSubstageStatus = (id, status) =>
    updateSubstageStatus(id, status).then(() =>
      _finishUpdate(__('project.substages.notification.success-update')),
    );

  const _updateStatus = (level, id, { status }) => {
    if (level === 2 || level === 3) {
      return _updatePlanningTask(id, { status });
    }

    return level === 0
      ? _setStageStatus(id, status)
      : _setSubstageStatus(id, status);
  };

  const _openLibraryPicker = (type, event) => {
    if (!focusedRow) return;

    const target = event?.currentTarget || event?.target;
    const rect = target?.getBoundingClientRect?.();
    const left = Math.max(
      10,
      Math.min(event?.clientX || rect?.left || 20, window.innerWidth - 370),
    );
    const top = Math.max(
      10,
      Math.min(rect?.bottom || event?.clientY || 70, window.innerHeight - 430),
    );

    hideMenu();
    setLibraryPicker({
      type,
      targetRow: focusedRow,
      left,
      top,
      key: `${Date.now()}-${type}`,
    });
  };

  const _createPlanningItems = (items) => {
    const picker = libraryPicker;

    if (!picker || _.isEmpty(items)) return;

    setLibraryPicker(undefined);

    let createItems;
    let notification;

    if (picker.type === 'substage') {
      createItems = _.reduce(
        items,
        (promise, item) =>
          promise.then(() =>
            createSubstage({
              stage_id: picker.targetRow.ownId,
              name: item.name,
              description: item.description || {},
              substage_template_id: item.id,
            }),
          ),
        Promise.resolve(),
      );
      notification = __('wizard.add-substage.notification.success');
    } else {
      const stageId = Number(_.first(_.split(picker.targetRow.id, '-')));
      const stage = _.find(table?.substages, { ownId: stageId });

      createItems = _.reduce(
        items,
        (promise, item) =>
          promise.then(() =>
            createTask(stageId, {
              type: 'group',
              title: getLocalized(item.name, mainLanguage) || '',
              description: item.description || {},
              status: 'not-started',
              priority: 'normal',
              visibility: _.toString(stage?.default_visibility ?? 1),
              group_template_id: item.id,
              substage_id: picker.targetRow.ownId,
              assignee: [],
              followers: [],
              stage_disciplines: [],
              tags: [],
            }),
          ),
        Promise.resolve(),
      );
      notification = __('wizard.add-task.confirm');
    }

    return createItems.then(() => {
      setNotification(notification);
      setExpandedRows((currentRows) =>
        _.union(currentRows || [], [picker.targetRow.id]),
      );
      listProjectSubstages(match.params.project);
    });
  };

  const activeView = _.get(table, 'view', 'compact');

  return (
    <div className={classNames(styles.table, filterStyles.wrapper)}>
      <div className={filterStyles.table}>
        <TitleBar
          title={__('submenu.title.project-planning')}
          dropdownHeader={__('submenu.title.project')}
          toolbar
        />

        <div className={styles.tableContent}>
          {hasPlanningModuleVisibility === false && (
            <div className={styles.noPermissionMessage}>
              {__('general.no-permission-to-access')}
            </div>
          )}
          {hasPlanningModuleVisibility && activeView === 'compact' && (
            <ProjectPlanningTable
              name='stages'
              templateCodes={templateCodes}
              title={__('submenu.title.project-planning')}
              expandedRows={expandedRows}
              localization={localization}
              data={table?.substages}
              filters={table?.filters}
              filterData={filterProjectSubstages}
              showFilters={table?.showFilters}
              listProjectSubstages={() => {
                listProjectSubstages(match.params.project);
              }}
              onExpandedRowsChange={(expanded_rows) => {
                setExpandedRows(expanded_rows);
              }}
              isProjectAdministrator={isProjectAdministrator}
              onFocusChange={setFocusedId}
              focusedId={focusedId}
              languageId={mainLanguage}
              disabledColumns={table?.disabled_columns ?? undefined}
              dropdownName={dropdownName}
              onDisabledColumnsChange={(disabled_columns) => {
                actions.setTableParams({ disabled_columns });
                updatePreferences('table.project_planning', {
                  disabled_columns,
                });
              }}
              onStatusChange={_updateStatus}
              onStageUpdate={_updateStage}
              onSubstageUpdate={_updateSubstage}
              onTaskUpdate={_updatePlanningTask}
              setFocusedContextMenuRow={setFocusedRow}
              onTaskOpen={openPlanningTask}
            />
          )}
        </div>

        {hasPlanningModuleVisibility &&
          table.clicked_task_row &&
          taskTable.clicked_row &&
          taskTable.flyout && (
            <div
              className={classNames(styles.flyout, styles.planningTaskFlyout)}
            >
              <TasksFlyout
                data={taskTable.flyout}
                lockTaskType
                onClose={closePlanningTask}
                readAction={(taskId) =>
                  readTask(taskId).then(({ response }) => {
                    listProjectSubstages(match.params.project);
                    return response;
                  })
                }
              />
            </div>
          )}

        {hasPlanningModuleVisibility && clicked_stage_row && (
          <div className={styles.flyout}>
            <StagesFlyout
              data={table.flyout}
              is_project_administrator={isProjectAdministrator}
              readAction={(stage_id) => {
                return new Promise(() => {
                  readStage(stage_id);
                  listProjectSubstages(match.params.project);
                });
              }}
            />
          </div>
        )}

        {hasPlanningModuleVisibility && clicked_substage_row && (
          <div className={styles.flyout}>
            <SubstagesFlyout
              language_id={mainLanguage}
              data={table.substage_flyout}
              is_project_administrator={isProjectAdministrator}
              readAction={(substage_id) => {
                return new Promise(() => {
                  readSubstage(substage_id).then(({ response }) => {
                    setTableParams('stages', {
                      substage_flyout: response.data,
                    });
                  });

                  listProjectSubstages(match.params.project);
                });
              }}
            />
          </div>
        )}

        <ContextMenu
          id='project_planning_context_menu'
          className={styles.contextMenu}
          onHide={() => setFocusedRow(undefined)}
        >
          {focusedRow && (
            <>
              <SubMenu
                title={
                  <div
                    className={classNames(
                      styles.contextMenuItem,
                      styles.leftSubmenuItem,
                    )}
                  >
                    <span>{__('planning-templates.builder.insert')}</span>
                    <ArrowRightSmallIcon />
                  </div>
                }
              >
                {focusedRow.level === 0 && (
                  <MenuItem
                    onClick={(event) => _openLibraryPicker('substage', event)}
                  >
                    <div
                      className={classNames(
                        styles.contextMenuItem,
                        styles.submenuItem,
                      )}
                    >
                      <span>{__('planning-templates.type.substage')}</span>
                      <span className={styles.contextMenuLevel}>L2</span>
                    </div>
                  </MenuItem>
                )}

                {focusedRow.level === 1 && (
                  <MenuItem
                    onClick={(event) => _openLibraryPicker('group', event)}
                  >
                    <div
                      className={classNames(
                        styles.contextMenuItem,
                        styles.submenuItem,
                      )}
                    >
                      <span>{__('planning-templates.type.group')}</span>
                      <span className={styles.contextMenuLevel}>L3</span>
                    </div>
                  </MenuItem>
                )}
              </SubMenu>
            </>
          )}
        </ContextMenu>

        {libraryPicker && (
          <ProjectPlanningLibraryDropdown
            key={libraryPicker.key}
            picker={libraryPicker}
            projectId={match.params.project}
            languageId={mainLanguage}
            onClose={() => setLibraryPicker(undefined)}
            onCreate={_createPlanningItems}
          />
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    table: state.table['stages'],
    taskTable: state.table.all_tasks,
    main_language: state.table['stages'].main_language,
    dropdownName: state.general.dropdown.shown,
    clicked_stage_row: _.get(state.table, ['stages', 'clicked_stage_row']),
    clicked_substage_row: _.get(state.table, [
      'stages',
      'clicked_substage_row',
    ]),
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    actions: {
      setTableParams: (params) =>
        dispatch({
          type: 'table.stages/SET_TABLE_PARAMS',
          params,
        }),
    },
  };
};

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(subscribe(Localization, 'localization')(ProjectPlanning)),
);
